import { useEffect, useRef } from 'react';

interface TurnstileApi { render(el: HTMLElement, opts: Record<string, unknown>): string; reset(id: string): void; remove(id: string): void }
declare global { interface Window { turnstile?: TurnstileApi } }

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;
function load(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not initialise')));
    s.onerror = () => { loading = null; reject(new Error('Turnstile failed to load')); };
    document.head.appendChild(s);
  });
  return loading;
}
/** The app writes its theme to <html data-theme>; the widget follows it. */
const theme = (): 'light' | 'dark' | 'auto' => { const t = document.documentElement.dataset.theme; return t === 'light' || t === 'dark' ? t : 'auto'; };

/**
 * Cloudflare Turnstile human check. `onToken` receives the single-use token once the challenge passes and null when it
 * expires, fails or the widget cannot load. Bump `resetKey` after a token was used to get a fresh challenge.
 */
export function Turnstile({ siteKey, onToken, resetKey = 0 }: { siteKey: string; onToken(token: string | null): void; resetKey?: number }) {
  const el = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const cb = useRef(onToken);
  cb.current = onToken;
  useEffect(() => {
    let cancelled = false;
    load()
      .then((api) => {
        if (cancelled || !el.current) return;
        widget.current = api.render(el.current, {
          sitekey: siteKey,
          theme: theme(),
          callback: (t: string) => cb.current(t),
          'expired-callback': () => cb.current(null),
          'timeout-callback': () => cb.current(null),
          'error-callback': () => { cb.current(null); return true; },
        });
      })
      .catch(() => cb.current(null));
    return () => {
      cancelled = true;
      if (widget.current) { window.turnstile?.remove(widget.current); widget.current = null; }
    };
  }, [siteKey]);
  useEffect(() => {
    if (resetKey && widget.current) { window.turnstile?.reset(widget.current); cb.current(null); }
  }, [resetKey]);
  return <div ref={el} className="turnstile" />;
}
